import { existsSync, mkdirSync, readFileSync, writeFileSync } from 'node:fs';
import { dirname } from 'node:path';
import packageJson from '../../package.json';
import { maxPath } from './paths.js';

const PACKAGE_NAME = '@manasija/max';
const REGISTRY_URL = 'https://registry.npmjs.org/@manasija%2Fmax/latest';
const CACHE_FILE = maxPath('update-check.json');
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;
const CURRENT_TTL_MS = 60 * 60 * 1000;
const DEFAULT_TIMEOUT_MS = 2500;
const INSTALL_COMMAND = 'npm install -g @manasija/max';

export type UpdateCheckResult =
  | { status: 'available'; currentVersion: string; latestVersion: string; command: string }
  | { status: 'current'; currentVersion: string; latestVersion: string }
  | { status: 'disabled' }
  | { status: 'skipped'; reason: string }
  | { status: 'error'; message: string };

export interface UpdateCheckOptions {
  nowMs?: number;
  ttlMs?: number;
  timeoutMs?: number;
  force?: boolean;
  fetchImpl?: typeof fetch;
  currentVersion?: string;
}

interface UpdateCache {
  checkedAtMs: number;
  result: Extract<UpdateCheckResult, { status: 'available' | 'current' }>;
}

interface RegistryLatestResponse {
  version?: unknown;
}

function parseVersion(version: string): [number, number, number] | null {
  const match = version.trim().match(/^(\d+)\.(\d+)\.(\d+)(?:[-+].*)?$/);
  if (!match) return null;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

export function compareSemver(a: string, b: string): number {
  const left = parseVersion(a);
  const right = parseVersion(b);
  if (!left || !right) return 0;
  for (let i = 0; i < 3; i += 1) {
    if (left[i] > right[i]) return 1;
    if (left[i] < right[i]) return -1;
  }
  return 0;
}

export async function checkForUpdate(options: UpdateCheckOptions = {}): Promise<UpdateCheckResult> {
  if (process.env.MAX_DISABLE_UPDATE_CHECK === '1') {
    return { status: 'disabled' };
  }

  const nowMs = options.nowMs ?? Date.now();
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const currentVersion = options.currentVersion ?? String(packageJson.version);
  if (!parseVersion(currentVersion)) {
    return { status: 'skipped', reason: `Invalid current version: ${currentVersion}` };
  }

  if (!options.force) {
    const cached = readCache();
    const cachedTtlMs = cached?.result.status === 'current' ? Math.min(ttlMs, CURRENT_TTL_MS) : ttlMs;
    if (cached && nowMs - cached.checkedAtMs < cachedTtlMs) {
      return cached.result;
    }
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);

  try {
    const fetchImpl = options.fetchImpl ?? fetch;
    const response = await fetchImpl(REGISTRY_URL, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      return { status: 'error', message: `npm registry returned ${response.status}` };
    }

    const data = await response.json() as RegistryLatestResponse;
    const latestVersion = typeof data.version === 'string' ? data.version : '';
    if (!parseVersion(latestVersion)) {
      return { status: 'skipped', reason: 'npm registry response did not include a valid version' };
    }

    const result: Extract<UpdateCheckResult, { status: 'available' | 'current' }> =
      compareSemver(latestVersion, currentVersion) > 0
        ? { status: 'available', currentVersion, latestVersion, command: INSTALL_COMMAND }
        : { status: 'current', currentVersion, latestVersion };
    writeCache({ checkedAtMs: nowMs, result });
    return result;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return { status: 'error', message };
  } finally {
    clearTimeout(timeout);
  }
}

function readCache(): UpdateCache | null {
  if (!existsSync(CACHE_FILE)) return null;
  try {
    const parsed = JSON.parse(readFileSync(CACHE_FILE, 'utf-8')) as UpdateCache;
    if (
      typeof parsed.checkedAtMs === 'number' &&
      parsed.result &&
      (parsed.result.status === 'available' || parsed.result.status === 'current')
    ) {
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
}

function writeCache(cache: UpdateCache): void {
  try {
    const dir = dirname(CACHE_FILE);
    if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
    writeFileSync(CACHE_FILE, JSON.stringify(cache, null, 2));
  } catch {
    // Best effort only.
  }
}

export const UPDATE_CHECK_PACKAGE_NAME = PACKAGE_NAME;
export const UPDATE_CHECK_INSTALL_COMMAND = INSTALL_COMMAND;
